import { LingAttribute } from './ling-attribute';
import {
  CertificateDynamicSectionContentType,
  CertificateKeyMetricType,
  CertificateStatus,
} from '../enums';
import { LingFile } from './ling-file';
import { LingEntity } from './ling-entity';
import { LingFileEntity } from './ling-file-entity';
import { CertificatePageConfigProfile } from './certificate-configuration';

export interface Image {
  _id: string;
  file: LingFile;
  isProcessing: boolean;
}

export interface CertificateGeneralSection {
  productTitle: string;
  productCreator: string;
  productDescription: string;
  images: Image[];
}

export interface CertificateKeyMetric {
  cardType: CertificateKeyMetricType;
  value: number;
  label: string;
}

export interface CertificateListItem {
  listItemType: ListItemType;
  key?: string;
  unit?: {
    _id: string;
    unit: string;
  };
  value?: string;
  iconKey?: string;
  link?: string;
}

export enum SimpleListRowDirections {
  Vertical = 'vertical',
  Horizontal = 'horizontal',
}

export enum DynamicSectionType {
  SimpleText = 'simple-text',
  BlockText = 'block-text',
  ProgressBar = 'progress-bar',
  ComplexList = 'complex-list',
  SimpleList = 'simple-list',
}

export enum ListItemType {
  Attribute = 'text',
  WebLink = 'url',
  Pdf = 'file',
}

export const ListItemTypeLabel: {
  [key in ListItemType]: string;
} = {
  [ListItemType.Attribute]: 'Attribute',
  [ListItemType.WebLink]: 'Web-Link',
  [ListItemType.Pdf]: 'PDF',
};

export interface ComplexListSection {
  title: string;
  subSections: ComplexListSubsection[];
}

export interface ComplexListSubsection {
  fields: CertificateListItem[];
  subSectionTitle: string;
}

export interface ProgressBar {
  origin: string;
  title: string;
  value: number;
  text: string;
}

export interface SimpleListSection {
  items: CertificateListItem[];
  title: string;
  rowDirection: SimpleListRowDirections;
}

export interface CertificateContentItem {
  order: number;
  contentType: CertificateDynamicSectionContentType;
  simpleText?: string;
  markdown?: string;
  progressBar?: ProgressBar;
  complexList?: ComplexListSection[];
  simpleList?: SimpleListSection[];
}

export interface CertificateDynamicSection {
  order: number;
  sectionName: string;
  // sectionIcon: {
  //   icon: string;
  //   source: string;
  // };
  sectionIcon: string;
  contentItems: CertificateContentItem[];
}

export interface CertificateMeasurementUnit {
  _id: string;
  unit: string;
}

export interface HeaderSettings {
  backgroundColor: string;
  logo: {
    file: File;
  };
}

export interface CertificateButton {
  label: string;
  baseUrl: string;
  finalUrl?: string;
}

/** Resolved company profile on certificate read (GET); read-only on write. */
export interface CertificateCompanyDetails {
  legalCompanyName: string;
  registeredAddress: string;
  dppContactEmail: string;
  uniqueManufacturerId: string;
  countryOfRegistration: string;
  defaultDataCarrier: string;
  defaultDppLanguage: string;
  dppContactPhone?: string | null;
  euImporterName?: string | null;
  euImporterIdentifier?: string | null;
}

/** Resolved facility on certificate read (GET); read-only on write. */
export interface CertificateFacilityDetails {
  facilityName: string;
  facilityId: string;
  countryOfManufacturer: string;
  fullAddress: string;
  isPrimary?: boolean;
}

export class Certificate extends LingEntity {
  name?: string;
  issuer?: { _id: string; name: string; pubKey: string };
  description?: string;
  tagId?: string;
  token?: string;
  tokenQr?: string;
  attributes: LingAttribute<any>[] = [];
  edition?: {
    _id: string;
    name: string;
  };
  status?: CertificateStatus;
  owner?: {
    _id?: string;
    name?: string;
    image?: LingFileEntity;
  };
  creator?: {
    _id?: string;
    name?: string;
    image?: LingFileEntity;
  };
  expirationDate?: Date;
  claimedDate?: Date;
  sentTo?: string;
  claimLink?: string;
  profile?: CertificatePageConfigProfile;
  general?: CertificateGeneralSection;
  certificateOfAuthenticityView?: boolean;
  keyMetrics?: CertificateKeyMetric[];
  dynamicSections?: CertificateDynamicSection[];
  issued?: string;
  headerSettings?: HeaderSettings;
  activeDraft?: string | null;
  guestEditToken?: string | null;
  guestEditTokenExpires?: string | null;
  buttons?: CertificateButton[];
  /** ObjectId refs; company auto-set on create. */
  company?: string;
  facility?: string | null;
  /** Populated on read for display only — do not send on PATCH/POST. */
  companyDetails?: CertificateCompanyDetails;
  facilityDetails?: CertificateFacilityDetails;

  constructor(certificate?: Certificate) {
    super();
    if (certificate) {
      Object.assign(this, certificate);
    }
    if (this.expirationDate) {
      this.expirationDate = new Date(this.expirationDate);
    }
    if (this.claimedDate) {
      this.claimedDate = new Date(this.claimedDate);
    }
  }
}
